import { h, relTime, uid } from '@/components/dom';
import { button, iconButton } from '@/components/controls';
import { icons, svgIcon } from '@/components/icons';
import { field, modal } from '@/components/modal';
import { ToastHost } from '@/components/toast';
import { imageFilesFromDataTransfer } from '@/image/decode';
import { SAMPLES, sampleThumb, type SampleSource } from '@/image/samples';
import { parseProject } from '@/export/project';
import { renderThumbnail } from '@/export/thumbnail';
import { byRecent, type DesignLibrary, type DesignRecord } from '@/state/library';
import { editorPath } from '@/router';
import { errorMessage } from '@/workers/workerProtocol';

export interface HomeDeps {
  library: DesignLibrary;
  go(path: string): void;
  /** A photo dropped or pasted on the home page; the caller opens it in a new design. */
  onPhoto(file: File): void;
}

export interface Mounted {
  destroy(): void;
}

const FEATURES: Array<[string, string, string]> = [
  [icons.wand, 'A real optimizer', 'Greedy search over every chord between pins, scored incrementally in Rust compiled to WebAssembly. The same seed gives the same strings.'],
  [icons.gauge, 'Never blocks', 'The engine runs in a Web Worker in 40 ms batches, so the page stays smooth while thousands of strings stream in.'],
  [icons.play, 'Watch it build', 'Play the construction string by string, scrub the timeline, and read the pins, error and gain of every one.'],
  [icons.edit, 'Edit the sequence', 'Select, delete, reorder, lock and add strings by hand, then regenerate from any point. Undo covers all of it.'],
  [icons.sliders, 'See the error', 'Original, processed target, live approximation, signed difference and heat map views under the strings.'],
  [icons.exportBox, 'Build it', 'PNG, SVG, DXF and G-code, with a physical plan that counts thread length, travel and pin visits.'],
];

const SORTS: Record<string, (a: DesignRecord, b: DesignRecord) => number> = {
  recent: byRecent,
  name: (a, b) => a.name.localeCompare(b.name),
  created: (a, b) => b.createdAt - a.createdAt,
};
const SORT_KEY = 'stringcanvas.sort';

/** The engine's own work: Cameron's portrait of Julia Jackson as 6,000 strings on 288 pins, rendered by the CLI. */
function heroArt(): HTMLElement {
  return h(
    'figure',
    { class: 'home__art' },
    h('img', { src: '/hero.webp', width: 900, height: 900, alt: 'String art of a woman in profile, made from a photograph by Julia Margaret Cameron', decoding: 'async' }),
    h('figcaption', null, '288 pins, 6,000 strings, one thread'),
  );
}

function sampleCard(sample: SampleSource, open: () => void): HTMLElement {
  const card = h('button', { type: 'button', class: 'tcard' }, h('span', { class: 'tcard__box' }, sampleThumb(sample, 192)), h('b', null, sample.label), h('i', null, sample.hint));
  card.addEventListener('click', open);
  return card;
}

function option(value: string, label: string): HTMLOptionElement {
  return h('option', { value }, label);
}

function readSort(): string {
  try {
    return localStorage.getItem(SORT_KEY) || 'recent';
  } catch {
    return 'recent';
  }
}

function hasFiles(e: DragEvent): boolean {
  return Array.from(e.dataTransfer?.types ?? []).includes('Files');
}

export async function mountHome(root: HTMLElement, deps: HomeDeps): Promise<Mounted> {
  document.title = 'String Canvas';
  const toasts = new ToastHost();
  const lifetime = new AbortController();
  const { signal } = lifetime;
  const state = { q: '', sort: readSort() };
  let designs: DesignRecord[] = [];

  const load = async () => {
    try {
      designs = await deps.library.list();
    } catch (err) {
      designs = [];
      toasts.show(`Could not read your designs: ${errorMessage(err)}`, 'error');
    }
  };

  const importProject = async (file: File) => {
    let project;
    try {
      project = parseProject(await file.text());
    } catch (err) {
      toasts.show(errorMessage(err), 'error');
      return;
    }
    const now = Date.now();
    const { width, height } = project.target;
    const record: DesignRecord = {
      id: uid(),
      name: file.name.replace(/\.json$/i, '').trim() || 'Imported design',
      createdAt: now,
      updatedAt: now,
      project: JSON.stringify(project),
      resolution: Math.max(width, height),
      cropMode: width === height ? 'square' : 'fit',
      sourceName: project.source.name,
      width,
      height,
      strings: project.sequence.length,
      pins: project.pinCoordinates.length,
      thumb: renderThumbnail(project),
    };
    try {
      await deps.library.put(record);
    } catch (err) {
      toasts.show(`Could not save the design: ${errorMessage(err)}`, 'error');
      return;
    }
    deps.go(editorPath(record.id));
  };

  const renameDesign = async (d: DesignRecord) => {
    const v = await modal({ title: 'Rename design', confirm: 'Rename', body: [field('Name', h('input', { name: 'name', value: d.name, required: true }))] });
    if (!v) return;
    const name = v.name.trim() || d.name;
    if (name === d.name) return;
    try {
      await deps.library.put({ ...d, name });
    } catch (err) {
      toasts.show(`Could not rename: ${errorMessage(err)}`, 'error');
      return;
    }
    await refresh();
  };

  const duplicateDesign = async (d: DesignRecord) => {
    const now = Date.now();
    const copy: DesignRecord = { ...d, id: uid(), name: `${d.name} copy`, createdAt: now, updatedAt: now };
    try {
      await deps.library.put(copy);
      const source = await deps.library.getSource(d.id);
      if (source) await deps.library.putSource(copy.id, source);
    } catch (err) {
      toasts.show(`Could not duplicate: ${errorMessage(err)}`, 'error');
      return;
    }
    await refresh();
  };

  const deleteDesign = async (d: DesignRecord) => {
    const v = await modal({ title: `Delete “${d.name}”?`, confirm: 'Delete', body: [h('p', { class: 'dim' }, 'This cannot be undone.')] });
    if (!v) return;
    try {
      await deps.library.remove(d.id);
    } catch (err) {
      toasts.show(`Could not delete: ${errorMessage(err)}`, 'error');
      return;
    }
    toasts.show('Deleted', 'info', 2000);
    await refresh();
  };

  const designCard = (d: DesignRecord): HTMLElement => {
    const thumb = h(
      'button',
      { type: 'button', class: 'pcard__thumb', style: { aspectRatio: `${d.width} / ${d.height}` }, attrs: { 'aria-label': `Open ${d.name}` } },
      d.thumb ? h('img', { src: d.thumb, alt: '' }) : h('span', null, 'No strings yet'),
    );
    thumb.addEventListener('click', () => deps.go(editorPath(d.id)));
    const acts = h(
      'span',
      { class: 'pcard__acts' },
      iconButton({ icon: icons.edit, title: 'Rename', onClick: () => void renameDesign(d) }),
      iconButton({ icon: icons.copy, title: 'Duplicate', onClick: () => void duplicateDesign(d) }),
      iconButton({ icon: icons.trash, title: 'Delete', danger: true, onClick: () => void deleteDesign(d) }),
    );
    const sub = [`${d.strings.toLocaleString()} ${d.strings === 1 ? 'string' : 'strings'}`, `${d.pins} pins`, `${d.width} × ${d.height}`, relTime(d.updatedAt)].join(', ');
    return h('article', { class: 'pcard' }, thumb, h('div', { class: 'pcard__row' }, h('b', { title: d.name }, d.name), acts), h('div', { class: 'pcard__sub' }, sub));
  };

  // ---- chrome
  const projectInput = h('input', { type: 'file', accept: '.json,application/json', style: { display: 'none' } });
  projectInput.addEventListener('change', () => {
    const f = projectInput.files?.[0];
    if (f) void importProject(f);
    projectInput.value = '';
  });
  const openBtn = button({ label: 'Open project', icon: icons.folder, title: 'Open a .json project file', onClick: () => projectInput.click() });
  const newBtn = button({ label: 'New design', variant: 'primary', icon: icons.plus, onClick: () => deps.go(editorPath(null)) });

  const count = h('span', { class: 'dim' });
  const search = h('input', { type: 'search', placeholder: 'Search designs', attrs: { 'aria-label': 'Search designs' } });
  const sort = h('select', { attrs: { 'aria-label': 'Sort designs' } }, option('recent', 'Recently edited'), option('name', 'Name'), option('created', 'Newest'));
  sort.value = state.sort;
  const tools = h('div', { class: 'home__tools' }, search, sort);
  const grid = h('div', { class: 'pgrid' });
  const empty = h('div', { class: 'home__empty' }, h('p', null, 'Nothing here yet. Start from a sample, drop a photo on this page, or open a project file.'));
  const veil = h('div', { class: 'dropveil' }, 'Drop the photo to start a design');

  const draw = () => {
    const q = state.q.trim().toLowerCase();
    const list = designs.filter((d) => !q || d.name.toLowerCase().includes(q)).sort(SORTS[state.sort] ?? SORTS.recent);
    tools.hidden = designs.length === 0;
    grid.hidden = designs.length === 0;
    empty.hidden = designs.length > 0;
    grid.replaceChildren(...(list.length > 0 ? list.map(designCard) : [h('p', { class: 'dim' }, 'No design matches.')]));
    count.textContent = designs.length === 0 ? 'none yet' : q ? `${list.length} of ${designs.length}` : String(designs.length);
  };
  const refresh = async () => {
    await load();
    draw();
  };

  search.addEventListener('input', () => {
    state.q = search.value;
    draw();
  });
  sort.addEventListener('change', () => {
    state.sort = sort.value;
    try {
      localStorage.setItem(SORT_KEY, state.sort);
    } catch {
      // a private window may refuse; the choice simply does not stick
    }
    draw();
  });

  const page = h(
    'div',
    { class: 'home' },
    h(
      'header',
      { class: 'home__top' },
      h('div', { class: 'home__brand' }, svgIcon(icons.mark), h('b', null, 'String Canvas'), h('span', null, 'by The Sketch Fam')),
      h('div', { class: 'home__acts' }, openBtn, newBtn, projectInput),
    ),
    h(
      'section',
      { class: 'home__hero' },
      h(
        'div',
        null,
        h('div', { class: 'home__eyebrow' }, 'String art from photographs'),
        h('h1', null, 'Every picture is', h('br'), 'one long ', h('em', null, 'thread'), '.'),
        h('p', null, 'Drop a photo, set the pins, and an optimizer written in Rust finds the strings that draw it, right here in the browser. Watch the thread go up string by string, edit the sequence, and export the plan for the frame. Nothing leaves your machine.'),
      ),
      heroArt(),
    ),
    h('section', { class: 'home__section' }, h('h2', null, 'Start from a sample'), h('div', { class: 'tgrid' }, ...SAMPLES.map((s) => sampleCard(s, () => deps.go(editorPath(null, s.id)))))),
    h('section', { class: 'home__section' }, h('h2', null, 'Your designs ', count), tools, grid, empty),
    h('section', { class: 'home__section' }, h('h2', null, 'What is inside'), h('div', { class: 'feat' }, ...FEATURES.map(([icon, title, text]) => h('article', null, svgIcon(icon), h('b', null, title), h('p', null, text))))),
    h('footer', { class: 'home__foot' }, 'Local first. Designs live in this browser until you export them.'),
  );

  // Photos dropped or pasted anywhere on the page start a design; project files are imported.
  let dragDepth = 0;
  document.addEventListener('dragenter', (e) => {
    if (!hasFiles(e)) return;
    dragDepth++;
    veil.classList.add('is-on');
  }, { signal });
  document.addEventListener('dragleave', () => {
    if (--dragDepth <= 0) {
      dragDepth = 0;
      veil.classList.remove('is-on');
    }
  }, { signal });
  document.addEventListener('dragover', (e) => {
    if (hasFiles(e)) e.preventDefault();
  }, { signal });
  document.addEventListener('drop', (e) => {
    dragDepth = 0;
    veil.classList.remove('is-on');
    if (!hasFiles(e)) return;
    e.preventDefault();
    const images = imageFilesFromDataTransfer(e.dataTransfer);
    if (images.length > 0) {
      deps.onPhoto(images[0]);
      return;
    }
    const project = Array.from(e.dataTransfer?.files ?? []).find((f) => /\.json$/i.test(f.name));
    if (project) void importProject(project);
    else toasts.show('Drop a photo or a .json project file.', 'error');
  }, { signal });
  document.addEventListener('paste', (e) => {
    const images = imageFilesFromDataTransfer(e.clipboardData);
    if (images.length > 0) {
      e.preventDefault();
      deps.onPhoto(images[0]);
    }
  }, { signal });

  await load();
  draw();
  root.replaceChildren(page, veil, toasts.element);
  return { destroy: () => lifetime.abort() };
}
